import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Globe, Clock, Mail, Instagram, ExternalLink } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { FooterSection } from "@/components/FooterSection";
import { SEOHead } from "@/components/SEOHead";
import { breadcrumbSchema } from "@/data/schemas";
import { InquiryForm } from "@/components/InquiryForm";

const GMB_LINK = "https://maps.app.goo.gl/ZBNHFw3Qe1MadnTM6";

export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="contact-page">
      <SEOHead
        title="Contact Emcee Sons - Visit, Call or WhatsApp"
        description="Contact Emcee Sons on Avenue Road, Bengaluru. Visit our showroom at Sri Krishna Building, call +91 93437 16059, or message on WhatsApp for diary orders and bulk enquiries. Open Mon-Sat 10:30 AM - 8:30 PM."
        path="/contact"
        schema={breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Contact" }])}
      />
      <Navbar />

      {/* Hero */}
      <div className="pt-20">
        <div className="bg-[#7A1A31] py-20 sm:py-28">
          <div className="container mx-auto px-6 sm:px-8 max-w-7xl text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-xs tracking-[0.25em] uppercase text-[#C8A23A] font-semibold mb-4 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Get in Touch</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }} data-testid="contact-page-heading">
                Contact Us
              </h1>
              <p className="text-base text-white/60 max-w-xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
                Visit our showroom, call us, or fill in your requirement below.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 sm:px-8 max-w-7xl py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info + Quick Links */}
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl text-[#1A1A1A] tracking-tight mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              How to <span className="text-[#7A1A31]">Reach Us</span>
            </h2>

            <div className="space-y-5">
              {[
                { icon: MapPin, title: "Our Address", content: "29, Avenue Rd, inside Sri Krishna Building, opposite to SBI, Cubbonpete, Bengaluru 560002", link: { text: "Get Directions", href: GMB_LINK } },
                { icon: Phone, title: "Phone", content: "+91 93437 16059", link: { text: "+91 93437 16059", href: "tel:+919343716059" } },
                { icon: Globe, title: "Website", content: "newyeardiaries.com", link: { text: "newyeardiaries.com", href: "http://www.newyeardiaries.com" } },
                { icon: Clock, title: "Business Hours", content: "Mon – Sat: 10:30 AM – 8:30 PM | Sunday: Closed" },
                { icon: Instagram, title: "Instagram", content: "@newyeardiaries", link: { text: "@newyeardiaries", href: "https://www.instagram.com/newyeardiaries/" } },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-black/5 rounded-xl p-5 flex gap-4 hover:shadow-lg hover:shadow-black/5 transition-all">
                  <div className="w-11 h-11 rounded-full bg-[#7A1A31]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4.5 h-4.5 text-[#7A1A31]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[#1A1A1A] mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{item.content}</p>
                    {item.link && (
                      <a href={item.link.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#7A1A31] hover:text-[#5C162E] mt-1 font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {item.link.text} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map below contact cards */}
            <div className="mt-8 rounded-xl overflow-hidden border border-black/5 shadow-lg shadow-black/5">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124415.89771793841!2d77.44098801640627!3d12.972056000000007!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae16098eb1714f%3A0xc06f7ed19606d01d!2sEagle%20Diaries-%20Emcee%20Sons!5e0!3m2!1sen!2sin!4v1775913193508!5m2!1sen!2sin"
                title="Eagle Diaries - Emcee Sons"
                data-testid="contact-map"
                className="w-full border-0"
                style={{ height: "300px" }}
                allowFullScreen
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Inquiry Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
            <h2 className="text-2xl text-[#1A1A1A] tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Send an <span className="text-[#7A1A31]">Inquiry</span>
            </h2>
            <p className="text-sm text-[#6B7280] mb-8" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
              Fill in your details and we'll get back to you within 24 hours.
            </p>
            <div className="bg-white border border-black/5 rounded-2xl p-8 sticky top-28">
              <InquiryForm source="contact" variant="light" />
            </div>
          </motion.div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}
